// Strips PHI and credentials out of everything that leaves the browser for a
// log or a third party: logger args (console + LogRocket replay lines), LogRocket
// network bodies, and URLs sent to LogRocket and Sentry.
//
// Backstop only. Never log PHI or credentials on purpose - log ids and counts
// instead. A masked value in dev output means the call site should log less, not
// that redaction made the line fine.
//
// No dependencies on purpose: it ships in the kiosk IIFE bundle via logger.js.

const MASK = '[redacted]'
// URL-safe, so a masked path segment or query value stays readable once encoded
const URL_MASK = 'REDACTED'
const MAX_DEPTH = 6
const MAX_STRING_LENGTH = 1000

const LOG = 'log'
const NETWORK = 'network'

/**
 * Keys that hold patient data wherever they appear in a payload. Matched exactly, so
 * `mrn_chars` on the hospital config is untouched while `mrn` is not.
 *
 * `hl7` carries the raw ADT message, whose PID segment contains full demographics.
 * `transcript`, `report`, the `*note` fields, `aimee_memory*`, `ai_snapshot*`, `away_message`,
 * and the Aimee chat `content`/`first_message` are free text written about or to a patient;
 * `precautions` and `pain_scale` are clinical state. `image` is a camera frame of the room.
 *
 * The patient fields mirror the Patient columns and `V1::PatientsController#patient_params`
 * — check both when they change, since the whole patient object is sent in several places.
 * Email is not PHI here: it only ever belongs to staff or guests.
 */
const PHI_KEYS = new Set([
  'display_name',
  'first_name',
  'last_name',
  'middle_name',
  'patient_name',
  'patient_names',
  'patient_display_name',
  'family_name',
  'given_name',
  'suffix',
  'mrn',
  'prior_mrn',
  'dob',
  'date_of_birth',
  'birth_date',
  'gender',
  'sex',
  'pronoun',
  'address',
  'phone',
  'patient_room',
  'note',
  'room_note',
  'family_note',
  'away_message',
  'aimee_memory',
  'aimee_memory_personal',
  'aimee_memory_clinical',
  'precautions',
  'pain_scale',
  'custom_fields',
  'ai_snapshot',
  'ai_snapshot_scorecard',
  'transcript',
  'report',
  'content',
  'first_message',
  'image',
  'hl7',
  'csn'
])

const CREDENTIAL_KEYS = new Set([
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'password',
  'secret',
  'authorization',
  'credential',
  'credentials'
])

// Ids are not PHI on their own, and logs need them for debugging. Network bodies
// mask them because LogRocket pairs them with everything else in the session.
const PATIENT_ID_KEYS = new Set(['patient_id', 'patient_monitor_id'])

// String values under these keys are run through maskUrl.
const URL_KEYS = new Set(['url', 'uri', 'href', 'redirect_uri'])

/**
 * Envelope keys that wrap the rows of a paginated response. They carry context
 * through to the rows, so that on a patients endpoint `results[].id` is the patient id.
 */
const PASSTHROUGH_KEYS = new Set(['results', 'data'])

/**
 * Keys that are harmless in general but hold patient data inside a particular payload.
 * A context is entered at the root when the request URL matches `rootUrl`, or below a key
 * listed in `containers`.
 *
 * `shallow` contexts apply only to the container's own fields (and its rows), because their
 * keys are everywhere: room.name, participant.name and tag.name stay readable. Other
 * contexts apply to the whole subtree, since their text sits under nested keys like
 * `payload` and `audited[].audited_changes`.
 */
const CONTEXTS = [
  {
    // Several endpoints serialize the patient name as a bare `name` (v1/virtual_requests/index,
    // v1/user_activities/_meeting, v1/patient_monitor_connections/index). Matches the
    // collection and a single nested segment (`/v1/patients`, `/v1/patients/4821`,
    // `/v1/patients/dropdown`) but not a sub-resource like `/v1/patients/4821/forms`.
    containers: ['patient', 'patients', 'dup_patient', 'patient_monitor', 'patient_monitors'],
    rootUrl: /\/v1\/(patients|patient_monitors)(\/[^/?#]+)?\/?(\?|#|$)/i,
    shallow: true,
    keys: ['name'],
    networkKeys: ['id']
  },
  {
    // Audit log messages end in patient initials and embed custom announcement text;
    // `details` holds old and new field values ("updated first name from ... to ...").
    // The timeline groups audit logs under these keys.
    containers: ['audit_logs', 'privacy_curtain', 'talk', 'video_visits', 'guest_video_visits', 'live_view', 'announcements', 'video_errors'],
    rootUrl: /\/v1\/(audit_logs|system_overview)\b/i,
    keys: ['message', 'details']
  },
  {
    // Stat alarm and AI alert text is written about the patient's event.
    containers: ['stat_alarms', 'alarms', 'events'],
    rootUrl: /\/v1\/(stat_alarms|patient_monitor_alerts|patient_monitors\/events)\b/i,
    keys: ['description', 'message', 'ai_analysis', 'ai_timeline']
  },
  {
    // Unmasked old and new values of any audited record, patients included.
    rootUrl: /\/v1\/revisions\//i,
    keys: ['old_value', 'new_value', 'related_object']
  },
  {
    // AI scene analysis of the patient's video.
    rootUrl: /\/v1\/recording_analyses\b/i,
    keys: ['result', 'log']
  }
]

/**
 * Query params we keep: navigation and pagination state that carries no patient data.
 * Everything else is masked rather than denylisted, because table filters put arbitrary
 * columns straight into the query string (mrn[0], patients.display_name[0], dob[0], ...),
 * so there is no fixed set of keys to exclude. Auth0's `code`/`state` are masked by this too.
 */
const PRESERVED_QUERY_KEYS = [
  'division_id',
  'page',
  'per_page',
  'menu_id',
  'item_id',
  'filter_id',
  'tab'
]

// uuid first, so a numeric prefix cannot partially match one; the lookahead keeps
// the match to a whole path segment
const ID_SEGMENT = '(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\\d+)(?=/|$)'

/**
 * Path segments identifying a patient or a monitoring session. Matching only id-shaped
 * segments leaves literal routes (/patients/add, /patient-monitors/multipause) readable.
 * The guest route is matched on any segment because that param is an access token.
 */
const PATH_RULES = [
  [new RegExp(`(/patients)/${ID_SEGMENT}`, 'gi'), `$1/${URL_MASK}`],
  // both spellings: the SPA routes use patient-monitors, the API uses patient_monitors
  [new RegExp(`(/patient[-_]monitors)/${ID_SEGMENT}`, 'gi'), `$1/${URL_MASK}`],
  [new RegExp(`(/digital-whiteboard/patient)/${ID_SEGMENT}`, 'gi'), `$1/${URL_MASK}`],
  [new RegExp(`(/digital-sign/patient)/${ID_SEGMENT}`, 'gi'), `$1/${URL_MASK}`],
  [new RegExp(`(/family-note)/${ID_SEGMENT}`, 'gi'), `$1/${URL_MASK}`],
  [new RegExp(`(/care-connect)/${ID_SEGMENT}`, 'gi'), `$1/${URL_MASK}`],
  [/(\/guest)\/[^/?#]+/gi, `$1/${URL_MASK}`]
]

// Only used to parse relative inputs; never returned, since those are rebuilt relative.
const PARSE_BASE = 'http://placeholder.invalid'

const NO_CONTEXTS = []

export class Redactor {
  // Returns a copy, so expanded console lines show log-time values.
  sanitizeLogArg(arg) {
    return this.redact(arg, { mode: LOG })
  }

  /**
   * Strips patient data out of a request or response body before it reaches LogRocket.
   * Keys are matched by name, so a payload that reaches PHI through a join under a key
   * not listed above will not be caught — add the key when that happens.
   *
   * A string body that is not JSON cannot be walked, so it is dropped rather than sent.
   *
   * @param {*} body - body as LogRocket supplies it (a JSON string), or empty
   * @param {string} [url] - the request URL, used to pick the payload's contexts
   */
  redactBody(body, url) {
    if (!body) {
      return body
    }

    const contexts = this.rootContexts(url)

    if (typeof body === 'string') {
      try {
        return JSON.stringify(this.redact(JSON.parse(body), { mode: NETWORK, contexts }))
      } catch {
        return MASK
      }
    }

    return this.redact(body, { mode: NETWORK, contexts })
  }

  /**
   * Removes PHI from a URL. Redacts patient and patient monitor ids from the path, and
   * masks every query param value outside PRESERVED_QUERY_KEYS — patient names, MRNs and
   * DOBs reach the URL as table filter values on listing pages.
   *
   * @param {string} urlString - absolute or relative URL
   * @returns {string} the masked URL, relative if the input was relative
   */
  maskUrl(urlString) {
    try {
      if (!urlString || typeof urlString !== 'string') {
        return urlString
      }

      const isRelative = !urlString.startsWith('http')
      if (isRelative && !urlString.startsWith('/')) {
        return urlString
      }
      const url = new URL(urlString, PARSE_BASE)

      url.pathname = PATH_RULES.reduce(
        (path, [pattern, replacement]) => path.replace(pattern, replacement),
        url.pathname
      )

      if (url.search) {
        const masked = new URLSearchParams()
        for (const [key, value] of new URLSearchParams(url.search).entries()) {
          masked.append(key, PRESERVED_QUERY_KEYS.includes(key) ? value : URL_MASK)
        }
        url.search = masked.toString()
      }

      return isRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
    } catch {
      return urlString
    }
  }

  // An AxiosError carries config.headers, including the Authorization bearer
  // token, and the request body. Keep only the fields worth reading.
  summarizeAxiosError(error, depth) {
    const url = error.config?.url
    return {
      status: error.response?.status,
      code: error.code,
      url: this.maskUrl(url),
      message: error.message,
      data: this.redact(error.response?.data, { mode: LOG, contexts: this.rootContexts(url), depth: depth + 1 })
    }
  }

  rootContexts(url) {
    if (typeof url !== 'string') {
      return NO_CONTEXTS
    }
    return CONTEXTS.filter((context) => context.rootUrl?.test(url))
  }

  redact(value, { mode, contexts = NO_CONTEXTS, depth = 0 }) {
    if (typeof value === 'string') {
      // top-level strings are the log message itself - never truncated
      if (mode === LOG && depth > 0) {
        return this.truncate(value)
      }
      return value
    }
    if (value === null || typeof value !== 'object') {
      return value
    }
    if (value.isAxiosError) {
      return this.summarizeAxiosError(value, depth)
    }
    if (value instanceof Error) {
      return value
    }
    if (mode === LOG && depth >= MAX_DEPTH) {
      return MASK
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.redact(item, { mode, contexts, depth: depth + 1 }))
    }
    return this.redactObject(value, { mode, contexts, depth })
  }

  redactObject(object, { mode, contexts, depth }) {
    const result = {}
    for (const [key, value] of Object.entries(object)) {
      const lowerKey = key.toLowerCase()

      if (value != null && this.isSensitive(lowerKey, mode, contexts)) {
        result[key] = MASK
      } else if (typeof value === 'string' && URL_KEYS.has(lowerKey)) {
        result[key] = this.maskUrl(value)
      } else {
        const childContexts = this.childContexts(lowerKey, contexts)
        result[key] = this.redact(value, { mode, contexts: childContexts, depth: depth + 1 })
      }
    }
    return result
  }

  isSensitive(key, mode, contexts) {
    if (PHI_KEYS.has(key) || CREDENTIAL_KEYS.has(key)) {
      return true
    }
    if (mode === NETWORK && PATIENT_ID_KEYS.has(key)) {
      return true
    }
    for (const context of contexts) {
      if (context.keys.includes(key)) {
        return true
      }
      if (mode === NETWORK && context.networkKeys?.includes(key)) {
        return true
      }
    }
    return false
  }

  // Contexts in effect below `key`: any it opens, plus the deep ones already open.
  // Shallow ones survive only a step into a rows envelope.
  childContexts(key, contexts) {
    const opened = CONTEXTS.filter((context) => context.containers?.includes(key))
    const kept = contexts.filter((context) => !context.shallow || PASSTHROUGH_KEYS.has(key))
    if (opened.length === 0) {
      return kept
    }
    return [...new Set([...kept, ...opened])]
  }

  truncate(text) {
    if (text.length <= MAX_STRING_LENGTH) {
      return text
    }
    return `${text.slice(0, MAX_STRING_LENGTH)}... [${text.length} chars]`
  }
}

export const redactor = new Redactor()
